import { errorBodySchema } from "@agw/contracts";
import type { z } from "zod";

/** Every failed call becomes an ApiError. `network` means no response at all (offline, DNS, CORS). */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details?: unknown,
    /** The API's request id (x-request-id), for "give the office this reference". */
    readonly requestId?: string,
  ) {
    super(message);
    this.name = "ApiError";
  }
  get isNetwork() {
    return this.code === "network";
  }
}

export interface FieldIssue {
  path: string;
  message: string;
}

/** Field issues from a 422 `validation` error, in the shape the core returns. */
export function fieldIssues(err: unknown): FieldIssue[] {
  if (!(err instanceof ApiError) || !Array.isArray(err.details)) return [];
  return err.details.filter(
    (d): d is FieldIssue => typeof d === "object" && d !== null && typeof (d as FieldIssue).path === "string" && typeof (d as FieldIssue).message === "string",
  );
}

/** "Check: Customer phone, VIN." A field with no label on screen also shows its message, so a hidden field is never silent. */
export function issuesBanner(issues: { path: string; message?: string }[], labels: Record<string, string>): string {
  const names = [...new Set(issues.map((i) => labels[i.path] ?? `${i.path}${i.message ? ` (${i.message})` : ""}`))];
  return names.length ? `Check: ${names.join(", ")}.` : "Some fields need attention.";
}

type Query = Record<string, string | number | undefined>;

interface RequestOptions<S extends z.ZodType | undefined> {
  body?: unknown;
  query?: Query;
  schema?: S;
  signal?: AbortSignal;
}

function withQuery(path: string, query?: Query) {
  if (!query) return path;
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) if (v !== undefined && v !== "") params.set(k, String(v));
  const qs = params.toString();
  return qs ? `${path}?${qs}` : path;
}

/**
 * Same-origin JSON request (CloudFront serves web and /api together), session cookie included.
 * The response is parsed with the contract schema, so a drifted API fails loudly here.
 */
export async function request<S extends z.ZodType | undefined = undefined>(
  method: "GET" | "POST" | "PUT" | "PATCH" | "DELETE",
  path: string,
  opts: RequestOptions<S> = {},
): Promise<S extends z.ZodType ? z.output<S> : unknown> {
  let res: Response;
  try {
    res = await fetch(withQuery(path, opts.query), {
      method,
      credentials: "include",
      headers: opts.body !== undefined ? { "content-type": "application/json", accept: "application/json" } : { accept: "application/json" },
      body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
      signal: opts.signal,
    });
  } catch (err) {
    if (err instanceof DOMException && err.name === "AbortError") throw err;
    throw new ApiError(0, "network", "No connection. Check your signal and try again.");
  }

  const text = await res.text();
  let json: unknown = undefined;
  if (text) {
    try {
      json = JSON.parse(text);
    } catch {
      json = undefined;
    }
  }

  if (!res.ok) {
    const rid = res.headers.get("x-request-id") ?? undefined;
    const parsed = errorBodySchema.safeParse(json);
    if (parsed.success) throw new ApiError(res.status, parsed.data.error.code, parsed.data.error.message, parsed.data.error.details, rid);
    throw new ApiError(res.status, res.status >= 500 ? "upstream" : "unknown", `Request failed (${res.status})`, undefined, rid);
  }

  if (!opts.schema) return json as never;
  const parsed = opts.schema.safeParse(json);
  if (!parsed.success) {
    console.error("[api] response did not match contract", path, parsed.error.issues);
    throw new ApiError(res.status, "contract", "The server sent an unexpected response.");
  }
  return parsed.data as never;
}

export interface ErrorView {
  kind: "offline" | "server" | "other";
  message: string;
  /** Reference id the office can look up in the API logs. */
  reference?: string;
}

const REF = /\s*Reference:\s*(\S+?)\.?$/;

/** What an error screen says: no signal, our fault (with a reference), or the API's own message. */
export function describeError(err: unknown, online = typeof navigator === "undefined" ? true : navigator.onLine): ErrorView {
  if (!online || (err instanceof ApiError && err.isNetwork)) {
    return { kind: "offline", message: "No signal. Check your connection, then try again." };
  }
  if (err instanceof ApiError && (err.status >= 500 || err.code === "contract")) {
    const reference = err.message.match(REF)?.[1] ?? err.requestId;
    return { kind: "server", message: "Something went wrong on our side. Try again in a minute. If it keeps happening, call the AGW office.", reference };
  }
  return { kind: "other", message: errorMessage(err).replace(REF, "") };
}

/** User-facing message for any error thrown by the client. */
export function errorMessage(err: unknown): string {
  if (err instanceof ApiError) return err.message;
  if (err instanceof Error) return err.message;
  return "Something went wrong.";
}
